import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header, { BreadcrumbItem } from '../components/Header';
import AboutModal from '../components/AboutModal';
import { useAuth } from '../context/AuthContext';
import { getUserState } from '../utils/userState';
import PublicLanding from '../components/PublicLanding';
import ReturningLanding from '../components/ReturningLanding';
import AuthenticatedHome from '../components/AuthenticatedHome';
import InvitationsPanel from '../components/InvitationsPanel';
import styles from '../styles/homePage.module.css';

interface HomePageProps {
  user?: any;
  onLogout: () => void;
  onLogin: () => void;
  onCreateAccount?: () => void;
}

const HomePage: React.FC<HomePageProps> = ({ user: userProp, onLogout, onLogin, onCreateAccount }) => {
  const { forceClearAuth, user: authUser } = useAuth();
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showInvitationsPanel, setShowInvitationsPanel] = useState(false);
  const [hasShownInvitation, setHasShownInvitation] = useState(false);
  
  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false }
  ];

  const state = getUserState(authUser);

  // Check for invitation token on mount
  useEffect(() => {
    const invitationToken = searchParams.get('invitation');
    if (invitationToken && !authUser && !hasShownInvitation) {
      // User clicked invitation link - open signup modal (only once)
      if (onCreateAccount) {
        onCreateAccount();
        setHasShownInvitation(true);
      }
    } else if (invitationToken && authUser) {
      // Already logged in user with invitation link - show invitations panel
      setShowInvitationsPanel(true);
    }
  }, [searchParams, authUser, onCreateAccount, hasShownInvitation]);

  const renderContent = () => {
    switch (state) {
      case "PUBLIC":
        return <PublicLanding onOpenAbout={() => setIsAboutOpen(true)} onLogin={onLogin} onCreateAccount={onCreateAccount} />;
      case "RETURNING":
        return <ReturningLanding onOpenAbout={() => setIsAboutOpen(true)} onLogin={onLogin} onCreateAccount={onCreateAccount} />;
      case "AUTHENTICATED":
        return <AuthenticatedHome />;
      default:
        return <PublicLanding onOpenAbout={() => setIsAboutOpen(true)} onLogin={onLogin} onCreateAccount={onCreateAccount} />;
    }
  };

  return (
    <div className={styles.homePage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={userProp}
        onLogout={onLogout}
        onLogin={onLogin}
      />
      
      <main className={styles.mainContent}>
        {renderContent()}
      </main>
      
      <AboutModal
        isOpen={isAboutOpen}
        onClose={() => setIsAboutOpen(false)}
      />
      
      <InvitationsPanel
        isOpen={showInvitationsPanel}
        onClose={() => setShowInvitationsPanel(false)}
        onInvitationResponded={() => {
          setShowInvitationsPanel(false);
        }}
      />
    </div>
  );
};

export default HomePage; 